import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { storytellersAPI } from "../services/api";

// Async thunks for storytellers
export const fetchStorytellers = createAsyncThunk(
  "storytellers/fetchStorytellers",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await storytellersAPI.getAll(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchPublishedStorytellers = createAsyncThunk(
  "storytellers/fetchPublishedStorytellers",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await storytellersAPI.getPublished(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchStorytellerById = createAsyncThunk(
  "storytellers/fetchStorytellerById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await storytellersAPI.getById(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createStoryteller = createAsyncThunk(
  "storytellers/createStoryteller",
  async (data, { rejectWithValue }) => {
    try {
      const response = await storytellersAPI.create(data);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateStoryteller = createAsyncThunk(
  "storytellers/updateStoryteller",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await storytellersAPI.update(id, data);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteStoryteller = createAsyncThunk(
  "storytellers/deleteStoryteller",
  async (id, { rejectWithValue }) => {
    try {
      await storytellersAPI.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const publishStoryteller = createAsyncThunk(
  "storytellers/publishStoryteller",
  async (id, { rejectWithValue }) => {
    try {
      const response = await storytellersAPI.publish(id);
      return { id, response };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const unpublishStoryteller = createAsyncThunk(
  "storytellers/unpublishStoryteller",
  async (id, { rejectWithValue }) => {
    try {
      const response = await storytellersAPI.unpublish(id);
      return { id, response };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchStorytellerStats = createAsyncThunk(
  "storytellers/fetchStorytellerStats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await storytellersAPI.getStats();
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Initial state
const initialState = {
  storytellers: [],
  publishedStorytellers: [],
  currentStoryteller: null,
  stats: null,
  loading: false,
  error: null,
  pagination: {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  },
  filters: {
    search: "",
    status: "",
    specialty: "",
    featured: "",
    popular: "",
  },
};

// Storytellers slice
const storytellersSlice = createSlice({
  name: "storytellers",
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentStoryteller: (state) => {
      state.currentStoryteller = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch storytellers
      .addCase(fetchStorytellers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStorytellers.fulfilled, (state, action) => {
        state.loading = false;
        state.storytellers = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchStorytellers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch published storytellers
      .addCase(fetchPublishedStorytellers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPublishedStorytellers.fulfilled, (state, action) => {
        state.loading = false;
        state.publishedStorytellers = action.payload.data;
      })
      .addCase(fetchPublishedStorytellers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch storyteller by ID
      .addCase(fetchStorytellerById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStorytellerById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentStoryteller = action.payload.data;
      })
      .addCase(fetchStorytellerById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create storyteller
      .addCase(createStoryteller.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createStoryteller.fulfilled, (state, action) => {
        state.loading = false;
        state.storytellers.unshift(action.payload.data);
      })
      .addCase(createStoryteller.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update storyteller
      .addCase(updateStoryteller.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateStoryteller.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.storytellers.findIndex(
          (storyteller) => storyteller.id === action.payload.data.id
        );
        if (index !== -1) {
          state.storytellers[index] = action.payload.data;
        }
        if (state.currentStoryteller?.id === action.payload.data.id) {
          state.currentStoryteller = action.payload.data;
        }
      })
      .addCase(updateStoryteller.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete storyteller
      .addCase(deleteStoryteller.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteStoryteller.fulfilled, (state, action) => {
        state.loading = false;
        state.storytellers = state.storytellers.filter(
          (storyteller) => storyteller.id !== action.payload
        );
      })
      .addCase(deleteStoryteller.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Publish storyteller
      .addCase(publishStoryteller.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(publishStoryteller.fulfilled, (state, action) => {
        state.loading = false;
        const storyteller = state.storytellers.find(
          (s) => s.id === action.payload.id
        );
        if (storyteller) {
          storyteller.status = "published";
          storyteller.published_at = new Date().toISOString();
        }
        if (state.currentStoryteller?.id === action.payload.id) {
          state.currentStoryteller.status = "published";
          state.currentStoryteller.published_at = new Date().toISOString();
        }
      })
      .addCase(publishStoryteller.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Unpublish storyteller
      .addCase(unpublishStoryteller.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(unpublishStoryteller.fulfilled, (state, action) => {
        state.loading = false;
        const storyteller = state.storytellers.find(
          (s) => s.id === action.payload.id
        );
        if (storyteller) {
          storyteller.status = "draft";
          storyteller.published_at = null;
        }
        if (state.currentStoryteller?.id === action.payload.id) {
          state.currentStoryteller.status = "draft";
          state.currentStoryteller.published_at = null;
        }
      })
      .addCase(unpublishStoryteller.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch storyteller stats
      .addCase(fetchStorytellerStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStorytellerStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload.data;
      })
      .addCase(fetchStorytellerStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setFilters, clearFilters, clearError, clearCurrentStoryteller } =
  storytellersSlice.actions;
export default storytellersSlice.reducer;
